'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// Mirror of the InstallerCrew type from the orchestrator. Kept local to avoid
// a circular import; the shape is stable enough that drift is unlikely, but
// any column addition should propagate here.
interface ComplianceIssue {
  type: string;
  severity: string;
  due_date: string;
}
interface CrewDocument {
  name: string;
  upload_date: string | null;
  expiration_date: string | null;
  status: 'valid' | 'expired' | 'pending';
}
export interface CrewForRow1 {
  id: string;
  company_name: string;
  foreman_name: string;
  compliance_status: string;
  compliance_issues: ComplianceIssue[];
  documents: CrewDocument[];
}

interface Props {
  crews: CrewForRow1[];
  onOpenCrew: (crewId: string) => void;
}

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

// ─── Date helpers ─────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;
function daysSinceIso(iso: string | null | undefined): number {
  if (!iso) return 0;
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
  const then  = new Date(iso.slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.floor((today - then) / DAY_MS);
}

// ─── Widget 1: Compliance Alert Center (real data) ────────────────────────

interface ComplianceAlert {
  crewId: string;
  companyName: string;
  foremanName: string;
  issueType: string;
  daysExpired: number;
  severity: string;
}

// Source priority: curated `compliance_issues` array first; fall back to
// walking the `documents` inventory for any doc with status='expired'.
function buildComplianceAlerts(crews: CrewForRow1[]): ComplianceAlert[] {
  const alerts: ComplianceAlert[] = [];
  for (const c of crews) {
    const hasCurated = c.compliance_issues && c.compliance_issues.length > 0;
    if (hasCurated) {
      for (const issue of c.compliance_issues) {
        const days = daysSinceIso(issue.due_date);
        alerts.push({
          crewId: c.id,
          companyName: c.company_name,
          foremanName: c.foreman_name,
          issueType: issue.type,
          daysExpired: Math.max(0, days),
          severity: issue.severity,
        });
      }
    } else {
      for (const doc of c.documents ?? []) {
        if (doc.status === 'expired' && doc.expiration_date) {
          const days = daysSinceIso(doc.expiration_date);
          alerts.push({
            crewId: c.id,
            companyName: c.company_name,
            foremanName: c.foreman_name,
            issueType: `${doc.name} Expired`,
            daysExpired: Math.max(0, days),
            severity: 'high',
          });
        }
      }
    }
  }
  // Sort worst first (most days expired).
  return alerts.sort((a, b) => b.daysExpired - a.daysExpired);
}

function severityPill(s: string): { color: string; bg: string } {
  const norm = (s || '').toLowerCase();
  if (norm === 'critical') return { color: '#7F1D1D', bg: 'rgba(239,68,68,0.18)' };
  if (norm === 'high')     return { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
  if (norm === 'medium')   return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                          { color: '#0369A1', bg: 'rgba(7,176,233,0.10)' };
}

function ComplianceAlertCenter({ crews, onOpenCrew }: Props) {
  const alerts = buildComplianceAlerts(crews);
  const top = alerts.slice(0, 6);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="ShieldExclamationIcon" size={15} style={{ color: '#EF4444' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Compliance Alerts
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold whitespace-nowrap">
          {alerts.length === 0 ? 'All crews compliant' : `${alerts.length} crew${alerts.length === 1 ? '' : 's'} need action`}
        </p>
      </div>
      {alerts.length === 0 ? (
        <div
          className="rounded-lg p-3 flex items-center gap-2"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)' }}
        >
          <Icon name="CheckCircleIcon" size={16} style={{ color: '#10B981' }} />
          <p className="text-xs font-medium" style={{ color: '#047857' }}>
            No expired W9 / insurance / license / bond across active crews
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {top.map((a, i) => {
            const sev = severityPill(a.severity);
            return (
              <div
                key={`${a.crewId}-${i}`}
                className="rounded-lg px-3 py-2"
                style={{ border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {a.companyName} <span className="text-gray-400 font-normal">· {a.foremanName}</span>
                  </p>
                  <span
                    className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                    style={{ background: sev.bg, color: sev.color }}
                  >
                    {a.severity.toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-gray-700 mb-1.5">
                  {a.issueType}{a.daysExpired > 0 && ` · ${a.daysExpired}d expired`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => console.log('[InstallerNetwork] Send reminder (no-op):', a.crewId)}
                    className="rounded-md px-2 py-1 text-[10px] font-semibold hover:opacity-90 active:scale-95 transition"
                    style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
                    suppressHydrationWarning
                  >
                    Send reminder
                  </button>
                  <button
                    onClick={() => onOpenCrew(a.crewId)}
                    className="rounded-md px-2 py-1 text-[10px] font-semibold hover:opacity-90 active:scale-95 transition"
                    style={{ background: '#F3F4F6', color: '#374151' }}
                    suppressHydrationWarning
                  >
                    View details
                  </button>
                </div>
              </div>
            );
          })}
          {alerts.length > top.length && (
            <p className="text-[10px] text-gray-400 text-center pt-1">
              + {alerts.length - top.length} more
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Widget 2: Onboarding Pipeline (mock, DEMO DATA pill) ────────────────

const STEP_LABELS = [
  'Company Profile',
  'Credentials & Insurance',
  'Service Area & Capacity',
  'Payment Setup',
  'Crew Management',
  'Complete',
] as const;

// Required steps in the contractor portal's 6-step flow: 1, 2, 3, 6.
// Optional steps: 4 (Stripe) and 5 (Crew Management).
const OPTIONAL_STEPS = new Set([4, 5]);

interface OnboardingCrew {
  company: string;
  foreman: string;
  currentStep: number;
  blocker: string;
  daysSinceLastSave: number;
}

const MOCK_ONBOARDING_CREWS: OnboardingCrew[] = [];

// Contractor portal lives on a different domain (separate repo, NestJS
// backend, Clerk auth). The link opens in a new tab so the CRM is not
// navigated away from.
//
// URL resolution: prefer NEXT_PUBLIC_CONTRACTOR_PORTAL_URL when the dev
// team sets it in Vercel. The fallback below is a *guess* — the actual
// production subdomain is not documented in this repo. Dev team should
// set the env var before this widget ships to production users, otherwise
// the "Resume onboarding →" link will 404.
const CONTRACTOR_ONBOARDING_URL =
  process.env.NEXT_PUBLIC_CONTRACTOR_PORTAL_URL ?? 'https://app.openbooq.com/onboarding';

function OnboardingPipeline() {
  const sorted = [...MOCK_ONBOARDING_CREWS].sort(
    (a, b) => b.daysSinceLastSave - a.daysSinceLastSave,
  );

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center justify-between gap-2 mb-1 pr-16">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="ClipboardDocumentListIcon" size={15} style={{ color: '#07B0E9' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Onboarding · {sorted.length} crews in progress
          </h3>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-3 pr-16">
        Read-only mirror of the contractor portal's 6-step flow ·{' '}
        <a
          href={CONTRACTOR_ONBOARDING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline"
          style={{ color: '#0369A1' }}
        >
          /onboarding ↗
        </a>
      </p>
      <div className="space-y-2">
        {sorted.map((c, i) => {
          const stepLabel = STEP_LABELS[c.currentStep - 1];
          const isOptional = OPTIONAL_STEPS.has(c.currentStep);
          const isStale = c.daysSinceLastSave > 7;
          const stepBg = isOptional ? 'rgba(107,114,128,0.10)' : 'rgba(7,176,233,0.10)';
          const stepColor = isOptional ? '#6B7280' : '#0369A1';
          return (
            <div
              key={`${c.company}-${i}`}
              className="rounded-lg px-3 py-2"
              style={{ border: '1px solid #F3F4F6' }}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {c.company} <span className="text-gray-400 font-normal">· {c.foreman}</span>
                </p>
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                  style={{ background: stepBg, color: stepColor }}
                  title={isOptional ? 'Optional step' : 'Required step'}
                >
                  Step {c.currentStep} of 6 · {stepLabel}{isOptional && ' (optional)'}
                </span>
              </div>
              <p className="text-[11px] text-gray-600 mb-1">{c.blocker}</p>
              <div className="flex items-center justify-between gap-2">
                <p
                  className="text-[10px] font-semibold"
                  style={{ color: isStale ? '#B91C1C' : '#6B7280' }}
                >
                  {c.daysSinceLastSave}d since last save{isStale && ' · stale'}
                </p>
                <a
                  href={CONTRACTOR_ONBOARDING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-semibold inline-flex items-center gap-0.5 hover:opacity-90"
                  style={{ color: '#0369A1' }}
                >
                  Resume onboarding
                  <Icon name="ArrowRightIcon" size={9} />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Row export ───────────────────────────────────────────────────────────

export default function Row1ComplianceAndOnboarding({ crews, onOpenCrew }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <ComplianceAlertCenter crews={crews} onOpenCrew={onOpenCrew} />
      <OnboardingPipeline />
    </section>
  );
}
